class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });
  }

  handleMegaItemActive(dropdown) {
    // Calculate backdrop height.
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme);
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this.onScrollHandler = this._onScroll.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Drawer open/close restores scroll. That is not the shopper scrolling,
    // so it must not unpin the bar. The builder options sheet has its own
    // scrollport — keep the site nav still while that sheet is open.
    if (typeof i9HeaderHideHeld === "function" && i9HeaderHideHeld()) {
      this.currentScrollTop = scrollTop;
      return;
    }

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    // Sticky pin/unpin moves the bar under a stationary cursor and fires a
    // synthetic hover enter — close mega so it cannot stick open while scrolling.
    if (
      this.classList.contains(this.classes.active) &&
      Math.abs(scrollTop - this.currentScrollTop) > 2
    ) {
      this._closeMenuDisclosure();
    }

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.stickyClasses.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.querySelector("site-nav");
    this.siteNav && this.siteNav.closeMegaDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
      }
    }
  }
}
customElements.define("header-menu", HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.megaItems = this.querySelectorAll(".f-site-nav__item--mega");
    this.nonMegaItems = this.querySelectorAll(
      ".f-site-nav__item--has-child:not(.f-site-nav__item--mega)"
    );
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this._pendingOpenItem = null;
    this._closeDeadline = null;
    this._navObservers = [];
    this.onDocPointerOver = this.onDocPointerOver.bind(this);
    this.onDocPointerLeave = this.onDocPointerLeave.bind(this);
    this.closeMegaDropdowns = this.closeMegaDropdowns.bind(this);
    this.onDocKeyDown = this.onDocKeyDown.bind(this);
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");
    this.overlay = this.header
      ? this.header.querySelector(".f-header__dropdown-overlay")
      : null;

    // Cursor may already sit on a trigger after click-through — mark those stale.
    this.armHoverMenus();

    if (this.isHover) {
      // One pointerover router — open/close from where the pointer is, not
      // paired enter/leave events (those miss scrim, logo, gap, wrap rows).
      document.addEventListener("pointerover", this.onDocPointerOver);
      document.documentElement.addEventListener(
        "mouseleave",
        this.onDocPointerLeave
      );

      this.nonMegaItems.forEach((item) => {
        const link = item.querySelector(".f-site-nav__link");
        // Keyboard: focus on compact/plain must dismiss the mega.
        if (link) link.addEventListener("focus", this.closeMegaDropdowns);
      });

      if (this.overlay) {
        this.overlay.addEventListener("click", this.closeMegaDropdowns);
      }
      document.addEventListener("keydown", this.onDocKeyDown);
    }

    this.bindOrbeaMenus();
  }

  getActiveMegaItem() {
    return this.querySelector(
      `.f-site-nav__item--mega.${this.classes.itemActive}`
    );
  }

  /**
   * Hover zones:
   *  1. Mega item (trigger or panel)     → keep / open
   *  2. Other nav item                   → short grace close (wrapped row)
   *  3. Header chrome (logo, gap band)   → long grace close
   *  4. Scrim / page                     → short grace close
   */
  onDocPointerOver(evt) {
    const t = evt.target;
    if (!t || !t.closest) return;

    const megaItem = t.closest(".f-site-nav__item--mega");
    if (megaItem && this.contains(megaItem)) {
      this.cancelClose();
      this.scheduleOpen(megaItem);
      return;
    }

    this.cancelOpen();

    // Wrapped second-row items sit between first-row triggers and the panel —
    // short grace so pass-through survives; scheduleClose re-checks :hover.
    if (t.closest(".f-site-nav__item")) {
      if (this.getActiveMegaItem()) this.scheduleClose(120);
      return;
    }

    if (!this.getActiveMegaItem()) return;

    const overOverlay =
      this.overlay && (t === this.overlay || this.overlay.contains(t));
    const inHeader = this.header && this.header.contains(t) && !overOverlay;
    this.scheduleClose(inHeader ? 350 : 150);
  }

  onDocPointerLeave() {
    if (this.getActiveMegaItem()) this.scheduleClose(150);
  }

  scheduleOpen(item) {
    if (this._staleHoverItems && this._staleHoverItems.has(item)) return;
    if (item.classList.contains(this.classes.itemActive)) return;
    if (this._pendingOpenItem === item) return;

    this.cancelOpen();
    const dropdown = item.querySelector(".f-site-nav__dropdown");
    if (!dropdown) return;

    // Cold open delays so grazing the bar does not flash; mega→mega is instant.
    const switching =
      this.getActiveMegaItem() ||
      (this.header && this.header.classList.contains("f-header__mega-active"));
    const delay = switching ? 0 : 120;

    this._pendingOpenItem = item;
    this.timeoutEnter = setTimeout(() => {
      this._pendingOpenItem = null;
      if (!item.matches(":hover")) return;
      if (this._staleHoverItems && this._staleHoverItems.has(item)) return;
      this.megaItems.forEach((megaItem) => {
        if (megaItem !== item) {
          megaItem.classList.remove(this.classes.itemActive);
        }
      });
      if (this.header) this.header.handleMegaItemActive(dropdown);
      item.classList.add(this.classes.itemActive);
    }, delay);
  }

  cancelOpen() {
    clearTimeout(this.timeoutEnter);
    this._pendingOpenItem = null;
  }

  /** Keep the earliest deadline — restarting on every pointerover would never close. */
  scheduleClose(delay) {
    const deadline = Date.now() + delay;
    if (this._closeDeadline !== null && this._closeDeadline <= deadline) return;
    clearTimeout(this.timeoutLeave);
    this._closeDeadline = deadline;
    this.timeoutLeave = setTimeout(() => {
      this._closeDeadline = null;
      const active = this.getActiveMegaItem();
      if (active && active.matches(":hover")) return;
      this.closeMegaDropdowns();
    }, delay);
  }

  cancelClose() {
    clearTimeout(this.timeoutLeave);
    this._closeDeadline = null;
  }

  /** After click-through, ignore the trigger the cursor is still sitting on. */
  armHoverMenus() {
    this._staleHoverItems = new Set();

    const markStale = (item) => {
      if (this._staleHoverItems.has(item)) return;
      this._staleHoverItems.add(item);
      item.classList.add("nav-hover-stale");
      const onLeave = () => {
        item.removeEventListener("mouseleave", onLeave);
        this._staleHoverItems.delete(item);
        item.classList.remove("nav-hover-stale");
      };
      item.addEventListener("mouseleave", onLeave);
    };

    const scan = () => {
      this.querySelectorAll(".f-site-nav__item--has-child").forEach((item) => {
        if (item.matches(":hover")) markStale(item);
      });
    };

    scan();
    requestAnimationFrame(() => {
      scan();
      if (this._staleHoverItems.size) {
        this.cancelOpen();
        this.closeMegaDropdowns();
      }
    });
  }

  bindOrbeaMenus() {
    this.querySelectorAll("[data-orbea-mega]").forEach((root) => {
      const cats = root.querySelectorAll("[data-orbea-cat]");
      const panels = root.querySelectorAll("[data-orbea-panel]");
      if (!cats.length || !panels.length) return;

      // Freeze category swaps while pointer is in products/rail.
      // Dwell before swapping so a diagonal path Gravel → product cards does
      // not steal the panel when the cursor crosses Dirt Jump / neighbors.
      const CAT_DWELL_MS = 220;
      let inResults = false;
      let pendingTimer = null;
      let activeId = null;

      const updateScrollHint = (grid) => {
        const wrap = grid.closest(".f-site-nav__orbea-products");
        if (!wrap) return;
        const more =
          grid.scrollHeight - grid.clientHeight - grid.scrollTop > 4;
        wrap.classList.toggle("has-scroll-more", more);
      };

      root.querySelectorAll(".f-site-nav__orbea-card-grid").forEach((grid) => {
        grid.addEventListener(
          "scroll",
          () => updateScrollHint(grid),
          { passive: true }
        );
        if (typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver(() => updateScrollHint(grid));
          observer.observe(grid);
          this._navObservers.push(observer);
        }
        updateScrollHint(grid);
      });

      const cancelPending = () => {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      };

      const activate = (id) => {
        cancelPending();
        if (!id || id === activeId) return;
        activeId = id;
        cats.forEach((cat) => {
          const on = cat.getAttribute("data-orbea-cat") === id;
          cat.classList.toggle("is-active", on);
          if (on) cat.setAttribute("aria-current", "true");
          else cat.removeAttribute("aria-current");
        });
        // Keep inactive panels in the DOM for CSS crossfade (no `hidden`).
        panels.forEach((panel) => {
          const on = panel.getAttribute("data-orbea-panel") === id;
          panel.classList.toggle("is-active", on);
          panel.setAttribute("aria-hidden", on ? "false" : "true");
          if ("inert" in panel) panel.inert = !on;
          if (on) {
            const grid = panel.querySelector(".f-site-nav__orbea-card-grid");
            if (grid) updateScrollHint(grid);
          }
        });
      };

      const scheduleActivate = (id) => {
        if (inResults || !id || id === activeId) {
          cancelPending();
          return;
        }
        cancelPending();
        pendingTimer = setTimeout(() => {
          pendingTimer = null;
          if (!inResults) activate(id);
        }, CAT_DWELL_MS);
      };

      const initial =
        root.querySelector("[data-orbea-cat].is-active") || cats[0];
      if (initial) activate(initial.getAttribute("data-orbea-cat"));

      cats.forEach((cat) => {
        const id = cat.getAttribute("data-orbea-cat");
        cat.addEventListener("mouseenter", () => scheduleActivate(id));
        // Keyboard / click: commit immediately — no dwell.
        cat.addEventListener("focus", () => activate(id));
        cat.addEventListener("click", (evt) => {
          const href = cat.getAttribute("href") || "";
          const isPlaceholder =
            !href ||
            href === "#" ||
            href.endsWith("/#") ||
            href.includes("javascript:");
          if (isPlaceholder) {
            evt.preventDefault();
            activate(id);
          }
        });
      });

      root
        .querySelectorAll(
          ".f-site-nav__orbea-col--products, .f-site-nav__orbea-col--rail"
        )
        .forEach((col) => {
          col.addEventListener("mouseenter", () => {
            inResults = true;
            cancelPending();
          });
          col.addEventListener("mouseleave", () => {
            inResults = false;
          });
        });
    });
  }

  onDocKeyDown(evt) {
    if (evt.key !== "Escape") return;
    if (!this.header || !this.header.classList.contains("f-header__mega-active"))
      return;
    evt.preventDefault();
    const trigger = this.querySelector(
      `.${this.classes.itemActive} > .f-site-nav__link`
    );
    this.closeMegaDropdowns();
    if (trigger) trigger.focus({ preventScroll: true });
  }

  closeMegaDropdowns() {
    this.cancelOpen();
    this.cancelClose();
    this.megaItems.forEach((megaItem) => {
      megaItem.classList.remove(this.classes.itemActive);
    });
    if (this.header) this.header.handleMegaItemDeactive();
  }

  disconnectedCallback() {
    this.cancelOpen();
    this.cancelClose();
    this._navObservers.forEach((observer) => observer.disconnect());
    this._navObservers = [];

    document.removeEventListener("pointerover", this.onDocPointerOver);
    document.documentElement.removeEventListener(
      "mouseleave",
      this.onDocPointerLeave
    );

    this.nonMegaItems.forEach((item) => {
      const link = item.querySelector(".f-site-nav__link");
      if (link) link.removeEventListener("focus", this.closeMegaDropdowns);
    });

    if (this.overlay) {
      this.overlay.removeEventListener("click", this.closeMegaDropdowns);
    }
    document.removeEventListener("keydown", this.onDocKeyDown);
  }
}
customElements.define("site-nav", SiteNav);
